import React, { useEffect, useState, useCallback } from 'react';
import { api, formatApiError } from '../../lib/api';
import { toast } from 'sonner';
import { Field } from './shared';
import { CalendarDays, Clock, MapPin, Users, Pencil, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

const EVENT_TYPES = [
  { id: 'visitation', label: 'Visitation / Viewing' },
  { id: 'funeral', label: 'Funeral Service' },
  { id: 'celebration_of_life', label: 'Celebration of Life' },
  { id: 'burial', label: 'Burial / Cemetery' },
  { id: 'reception', label: 'Reception' },
  { id: 'custom', label: 'Custom Service Event' },
];

const typeLabel = (id) => EVENT_TYPES.find((t) => t.id === id)?.label || 'Service Event';

const EMPTY = {
  event_type: 'funeral',
  name: '',
  date: '',
  time: '',
  location_name: '',
  address: '',
  notes: '',
  rsvp_enabled: true,
};

export function ServicesTab({ memorialId }) {
  const [services, setServices] = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | service_id

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/memorials/${memorialId}/services`);
      setServices(data.services || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [memorialId]);
  useEffect(() => { load(); }, [load]);

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"? RSVPs for this event will also be removed.`)) return;
    try {
      await api.delete(`/services/${s.service_id}`);
      toast.success('Service event deleted');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!services) {
    return <div className="ml-card text-sm text-[hsl(var(--muted-foreground))]">Loading…</div>;
  }

  return (
    <div className="space-y-5" data-testid="services-tab-panel">
      <div className="ml-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-serif text-2xl text-[hsl(var(--primary))]">Upcoming Services</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-xl leading-relaxed">
              Visitation, funeral, celebration of life, burial, and reception details
              appear in an "Upcoming Services" section near the top of the public
              memorial — with directions and an optional RSVP for visitors.
            </p>
          </div>
          {editing === null && (
            <button
              onClick={() => setEditing('new')}
              className="ml-btn-primary"
              data-testid="add-service-btn"
            >
              Add service event
            </button>
          )}
        </div>
        {editing === 'new' && (
          <ServiceForm
            memorialId={memorialId}
            initial={EMPTY}
            onDone={() => { setEditing(null); load(); }}
            onCancel={() => setEditing(null)}
          />
        )}
      </div>

      {services.length === 0 && editing !== 'new' && (
        <div className="ml-card text-sm text-[hsl(var(--muted-foreground))]" data-testid="services-empty">
          No service events yet. Add the visitation, funeral, or celebration of
          life details so visitors know when and where to gather.
        </div>
      )}

      {services.map((s) => (
        <div key={s.service_id} className="ml-card" data-testid={`admin-service-${s.service_id}`}>
          {editing === s.service_id ? (
            <ServiceForm
              memorialId={memorialId}
              service={s}
              initial={{
                event_type: s.event_type || 'custom',
                name: s.name || '',
                date: s.date || '',
                time: s.time || '',
                location_name: s.location_name || '',
                address: s.address || '',
                notes: s.notes || '',
                rsvp_enabled: s.rsvp_enabled !== false,
              }}
              onDone={() => { setEditing(null); load(); }}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <ServiceRow
              service={s}
              onEdit={() => setEditing(s.service_id)}
              onDelete={() => remove(s)}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function ServiceRow({ service: s, onEdit, onDelete }) {
  const [showRsvps, setShowRsvps] = useState(false);
  const counts = s.rsvp_counts || { attending: 0, maybe: 0, declined: 0 };
  const totalRsvps = (s.rsvps || []).length;
  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] tracking-[0.24em] uppercase font-semibold text-[hsl(var(--accent))]">
            {typeLabel(s.event_type)}
          </div>
          <div className="font-serif text-xl text-[hsl(var(--primary))] mt-1">{s.name}</div>
          <div className="mt-2 space-y-1 text-sm text-[hsl(var(--primary)/0.75)]">
            {(s.date || s.time) && (
              <div className="flex items-center gap-2">
                <CalendarDays className="w-3.5 h-3.5 text-[hsl(var(--accent))]" />
                {s.date ? new Date(`${s.date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : '—'}
                {s.time && (<><Clock className="w-3.5 h-3.5 text-[hsl(var(--accent))] ml-2" /> {s.time}</>)}
              </div>
            )}
            {(s.location_name || s.address) && (
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-[hsl(var(--accent))]" />
                {[s.location_name, s.address].filter(Boolean).join(' · ')}
              </div>
            )}
            {s.notes && <div className="italic text-xs text-[hsl(var(--muted-foreground))]">{s.notes}</div>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={onEdit} className="ml-btn-outline !py-2 text-xs inline-flex items-center gap-1.5" data-testid={`edit-service-${s.service_id}`}>
            <Pencil className="w-3.5 h-3.5" /> Edit
          </button>
          <button onClick={onDelete} className="ml-btn-outline !py-2 text-xs !border-red-600/40 !text-red-700 inline-flex items-center gap-1.5" data-testid={`delete-service-${s.service_id}`}>
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </div>

      {/* RSVP counts — admin-only */}
      <div className="mt-4 pt-4 border-t border-[hsl(var(--primary)/0.08)]">
        <div className="flex flex-wrap items-center gap-3" data-testid={`rsvp-counts-${s.service_id}`}>
          <Users className="w-4 h-4 text-[hsl(var(--accent))]" />
          {s.rsvp_enabled === false ? (
            <span className="text-xs text-[hsl(var(--muted-foreground))]">RSVP is turned off for this event.</span>
          ) : (
            <>
              <span className="text-xs font-semibold text-green-800 bg-green-50 border border-green-700/30 rounded-full px-2.5 py-1">
                {counts.attending} attending
              </span>
              <span className="text-xs font-semibold text-[hsl(var(--accent))] bg-[hsl(var(--accent))]/8 border border-[hsl(var(--accent))]/40 rounded-full px-2.5 py-1">
                {counts.maybe} maybe
              </span>
              <span className="text-xs font-semibold text-[hsl(var(--primary)/0.6)] bg-[hsl(var(--muted))] border border-[hsl(var(--primary)/0.12)] rounded-full px-2.5 py-1">
                {counts.declined} unable to attend
              </span>
              {totalRsvps > 0 && (
                <button
                  onClick={() => setShowRsvps((v) => !v)}
                  className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[hsl(var(--accent))] hover:text-[hsl(var(--primary))] inline-flex items-center gap-1"
                  data-testid={`toggle-rsvps-${s.service_id}`}
                >
                  {showRsvps ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  {showRsvps ? 'Hide names' : 'See who responded'}
                </button>
              )}
            </>
          )}
        </div>
        {showRsvps && totalRsvps > 0 && (
          <div className="mt-3 space-y-1.5" data-testid={`rsvp-list-${s.service_id}`}>
            {(s.rsvps || []).map((r) => (
              <div key={r.rsvp_id} className="flex flex-wrap items-center gap-2 text-xs text-[hsl(var(--primary)/0.8)]">
                <span className={`w-1.5 h-1.5 rounded-full ${r.status === 'attending' ? 'bg-green-600' : r.status === 'maybe' ? 'bg-[hsl(var(--accent))]' : 'bg-[hsl(var(--primary)/0.35)]'}`} />
                <span className="font-medium">{r.name}</span>
                {r.email && <span className="text-[hsl(var(--muted-foreground))]">{r.email}</span>}
                <span className="uppercase tracking-widest text-[10px] text-[hsl(var(--muted-foreground))]">
                  {r.status === 'declined' ? 'unable to attend' : r.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ServiceForm({ memorialId, service, initial, onDone, onCancel }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!f.name.trim()) {
      toast.error('Please give the service event a name.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        event_type: f.event_type,
        name: f.name.trim(),
        date: f.date || null,
        time: f.time,
        location_name: f.location_name,
        address: f.address,
        notes: f.notes,
        rsvp_enabled: f.rsvp_enabled,
      };
      if (service) {
        await api.patch(`/services/${service.service_id}`, body);
        toast.success('Service event updated');
      } else {
        await api.post(`/memorials/${memorialId}/services`, body);
        toast.success('Service event added');
      }
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-5 pt-5 border-t border-[hsl(var(--primary)/0.08)]" data-testid="service-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Event type">
          <select
            className="ml-input"
            value={f.event_type}
            onChange={(e) => set('event_type', e.target.value)}
            data-testid="service-form-type"
          >
            {EVENT_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="Event name">
          <input
            className="ml-input"
            value={f.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="e.g. Funeral Mass for Michael"
            maxLength={160}
            data-testid="service-form-name"
          />
        </Field>
        <Field label="Date">
          <input
            type="date"
            className="ml-input"
            value={f.date}
            onChange={(e) => set('date', e.target.value)}
            data-testid="service-form-date"
          />
        </Field>
        <Field label="Time">
          <input
            className="ml-input"
            value={f.time}
            onChange={(e) => set('time', e.target.value)}
            placeholder="e.g. 2:00 PM – 4:00 PM"
            maxLength={80}
            data-testid="service-form-time"
          />
        </Field>
        <Field label="Location name">
          <input
            className="ml-input"
            value={f.location_name}
            onChange={(e) => set('location_name', e.target.value)}
            placeholder="e.g. St. Mary's Church"
            maxLength={200}
            data-testid="service-form-location"
          />
        </Field>
        <Field label="Address">
          <input
            className="ml-input"
            value={f.address}
            onChange={(e) => set('address', e.target.value)}
            placeholder="Street, city, state — powers Get Directions"
            maxLength={300}
            data-testid="service-form-address"
          />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Notes for visitors (optional)">
            <textarea
              rows={2}
              className="ml-input"
              value={f.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="e.g. Light refreshments to follow in the parish hall."
              maxLength={600}
              data-testid="service-form-notes"
            />
          </Field>
        </div>
      </div>
      <label className="mt-4 flex items-center gap-2.5 text-sm text-[hsl(var(--primary))] cursor-pointer">
        <input
          type="checkbox"
          checked={f.rsvp_enabled}
          onChange={(e) => set('rsvp_enabled', e.target.checked)}
          data-testid="service-form-rsvp-toggle"
        />
        Allow visitors to RSVP (Attending / Maybe / Unable to attend)
      </label>
      <div className="mt-5 flex gap-3">
        <button onClick={save} disabled={saving} className="ml-btn-primary" data-testid="service-form-save">
          {saving ? 'Saving…' : (service ? 'Save changes' : 'Add service event')}
        </button>
        <button onClick={onCancel} className="ml-btn-outline" data-testid="service-form-cancel">
          Cancel
        </button>
      </div>
    </div>
  );
}
